import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Sidebar from "./Sidebar";

const DashboardLayout = ({ children, allowPublic = false }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;

        if (!session?.user) {
          const isGuest =
            localStorage.getItem("guest") === "1" ||
            localStorage.getItem("guest_mode") === "true";

          if (!isGuest && !allowPublic) {
            navigate("/login");
            return;
          }
          // Guest mode or public access: no user session
          setUser(null);
        } else {
          // Real user session
          setUser(session.user);
        }
      } catch (error) {
        console.error("Session check error:", error);
        // Check for guest mode even on error
        const isGuest =
          localStorage.getItem("guest") === "1" ||
          localStorage.getItem("guest_mode") === "true";
        if (!isGuest && !allowPublic) {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, [navigate, allowPublic]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchUnread = async () => {
      const { count, error } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        console.error("Unread count error:", error);
        return;
      }
      setUnreadCount(count ?? 0);
    };

    fetchUnread();

    const ch = supabase
      .channel(`layout-notif-count-realtime-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        fetchUnread,
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
  }, [user?.id]);

  const handleLogout = async () => {
    try {
      // Clear guest flags
      localStorage.removeItem("guest");
      localStorage.removeItem("guest_mode");
      window.dispatchEvent(new Event("guest-mode-change"));

      // Sign out
      await supabase.auth.signOut();

      // Use window.location for a hard navigation that bypasses route guards
      window.location.href = "/about-team";
    } catch (err) {
      console.error("Logout failed", err);
      // Even if signOut fails, navigate to about-team
      window.location.href = "/about-team";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "background.default",
        minHeight: "100vh",
      }}
    >
      <Sidebar
        isGuest={!user}
        onLogout={handleLogout}
        unreadCount={unreadCount}
      />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Pass user to children if they need it */}
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { user, onLogout: handleLogout });
          }
          return child;
        })}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
